import { editorVar } from 'gql/local';

export const initialState = {
  status: null,
  questionsSelected: [],
  chapterSelected: null,
  commentSelected: null,
  questionType: null,
  questionEditing: null,
  folderEditing: null,
  chapterEditing: null,
  gameEditing: null,
  collaboratorEditing: null,
  groupEditing: null,
  studentEditing: null,
};

export function mutationEditorReset() {
  editorVar(initialState);
}

export function mutationEditorClear() {
  const editor = editorVar();
  editorVar({
    ...editor,
    status: null,
    questionsSelected: [],
    questionType: null,
    questionEditing: null,
    folderEditing: null,
    chapterEditing: null,
    gameEditing: null,
    collaboratorEditing: null,
    groupEditing: null,
    studentEditing: null,
  });
}

export function mutationEditorQuestionCreate(questionType) {
  const editor = editorVar();
  editorVar({
    ...editor,
    status: 'question-create',
    questionsSelected: [],
    questionType: typeof questionType === 'string' ? questionType : null,
    questionEditing: {
      content: {},
    },
  });
}

export function mutationEditorQuestionCreateFromIA() {
  const editor = editorVar();
  editorVar({
    ...editor,
    status: 'question-create-from-ia',
    questionsSelected: [],
    questionType: null,
    questionEditing: null,
  });
}

export function mutationEditorQuestionPropose(questionType) {
  const editor = editorVar();
  editorVar({
    ...editor,
    status: 'question-propose',
    questionsSelected: [],
    questionType: typeof questionType === 'string' ? questionType : null,
    questionEditing: {
      content: {},
    },
  });
}

export function mutationEditorQuestionVerify(question) {
  const editor = editorVar();
  editorVar({
    ...editor,
    status: 'question-approve',
    questionsSelected: [question],
    questionType: question.type,
    questionEditing: question,
  });
}

export function mutationEditorQuestionUpdate(question) {
  const editor = editorVar();
  editorVar({
    ...editor,
    status: 'question-update',
    questionsSelected: [question],
    chapterSelected:
      editor.chapterSelected && editor.chapterSelected.id === question.chapterId
        ? editor.chapterSelected
        : null,
    questionType: question.type,
    questionEditing: question,
  });
}

export function mutationEditorQuestionImport() {
  const editor = editorVar();
  editorVar({
    ...editor,
    status: 'question-import',
  });
}

export function mutationEditorQuestionImportPreview(question) {
  const editor = editorVar();
  editorVar({
    ...editor,
    questionEditing: question,
  });
}

export function mutationEditorQuestionSelect(question) {
  const editor = editorVar();
  const questionsSelected = editor.questionsSelected.some(
    (q) => q.id === question.id,
  )
    ? editor.questionsSelected.filter((q) => q.id !== question.id)
    : [...editor.questionsSelected, question];
  editorVar({
    ...editor,
    status: questionsSelected.length ? 'question-multi' : null,
    questionsSelected,
  });
}

export function mutationEditorQuestionSelectMultiple({
  lastQuestion,
  question,
  questions,
}) {
  const editor = editorVar();
  const questionsToSelect = questions
    .filter((q) =>
      lastQuestion.order < question.order
        ? q.order > lastQuestion.order && q.order <= question.order
        : q.order < lastQuestion.order && q.order >= question.order,
    )
    .filter(
      (qToSelect) =>
        !editor.questionsSelected.some((q) => q.id === qToSelect.id),
    );
  const questionsSelected = [...editor.questionsSelected, ...questionsToSelect];
  editorVar({
    ...editor,
    status: questionsSelected.length ? 'question-multi' : null,
    questionsSelected,
  });
}

export function mutationEditorQuestionTypeChange(questionType) {
  const editor = editorVar();
  editorVar({
    ...editor,
    questionType,
  });
}

export function mutationEditorQuestionMove(question) {
  const editor = editorVar();
  editorVar({
    ...editor,
    status: 'question-move',
    questionsSelected: [question],
    questionEditing: question,
  });
}

export function mutationEditorQuestionsMove() {
  const editor = editorVar();
  editorVar({
    ...editor,
    status: 'questions-move',
  });
}

export function mutationEditorQuestionPreview(question) {
  const editor = editorVar();
  editorVar({
    ...editor,
    status: 'question-preview',
    questionsSelected: [question],
    questionEditing: question,
  });
}

export function mutationEditorQuestionComments(question) {
  const editor = editorVar();
  editorVar({
    ...editor,
    status: 'question-comments',
    questionsSelected: [question],
    commentSelected: null,
    questionEditing: question,
  });
}

export function mutationEditorStudentErrors(question) {
  const editor = editorVar();
  editorVar({
    ...editor,
    status: 'student-errors',
    questionEditing: question,
  });
}

export function mutationEditorReport() {
  const editor = editorVar();
  editorVar({
    ...editor,
    status: 'report',
  });
}

export function mutationEditorAggregatedAnswers(question) {
  const editor = editorVar();
  editorVar({
    ...editor,
    status: 'aggregated-answers',
    questionEditing: question,
  });
}

export function mutationEditorQuestionCommentsChange(comment) {
  const editor = editorVar();
  editorVar({
    ...editor,
    commentSelected: comment,
  });
}

export function mutationEditorFolderUpdate(folder) {
  const editor = editorVar();
  editorVar({
    ...editor,
    status: 'folder-update',
    folderEditing: folder,
  });
}

export function mutationEditorFolderDelete(folder) {
  const editor = editorVar();
  editorVar({
    ...editor,
    status: 'folder-delete',
    folderEditing: folder,
  });
}

export function mutationEditorChapterCreate() {
  const editor = editorVar();
  editorVar({
    ...editor,
    status: 'chapter-create',
    questionsSelected: [],
    chapterSelected: null,
    questionEditing: null,
    chapterEditing: {},
  });
}

export function mutationEditorChapterUpdate(chapter) {
  const editor = editorVar();
  editorVar({
    ...editor,
    status: 'chapter-update',
    questionsSelected: [],
    chapterSelected: chapter,
    questionEditing: null,
    chapterEditing: chapter,
  });
}

export function mutationEditorChapterSelect(chapter) {
  const editor = editorVar();
  editorVar({
    ...editor,
    chapterSelected: editor.chapterSelected?.id === chapter.id ? null : chapter,
  });
}

export function mutationEditorChapterMove(chapter) {
  const editor = editorVar();
  editorVar({
    ...editor,
    status: 'chapter-move',
    questionsSelected: [],
    chapterSelected:
      editor.chapterSelected?.id === chapter.id ? null : editor.chapterSelected,
    questionEditing: null,
    chapterEditing: chapter,
  });
}

export function mutationEditorGameCreate() {
  const editor = editorVar();
  editorVar({
    ...editor,
    status: 'game-create',
    questionsSelected: [],
    chapterSelected: null,
  });
}

export function mutationEditorGameDelete(game) {
  const editor = editorVar();
  editorVar({
    ...editor,
    status: 'game-delete',
    questionsSelected: [],
    chapterSelected: null,
    gameEditing: game,
  });
}

export function mutationEditorGameUpdate(game) {
  const editor = editorVar();
  editorVar({
    ...editor,
    status: 'game-update',
    questionsSelected: [],
    chapterSelected: null,
    gameEditing: game,
  });
}

export function mutationEditorCollaboratorsInvite() {
  const editor = editorVar();
  editorVar({
    ...editor,
    status: 'collaborators-invite',
  });
}

export function mutationEditorCollaboratorDelete(collaborator) {
  const editor = editorVar();
  editorVar({
    ...editor,
    status: 'collaborator-delete',
    questionsSelected: [],
    chapterSelected: null,
    collaboratorEditing: collaborator,
  });
}

export function mutationEditorGroupUpdate(group) {
  const editor = editorVar();
  editorVar({
    ...editor,
    status: 'group-update',
    groupEditing: group,
  });
}

export function mutationEditorGroupShare(group) {
  const editor = editorVar();
  editorVar({
    ...editor,
    status: 'group-share',
    groupEditing: group,
  });
}

export function mutationEditorGroupDelete(group) {
  const editor = editorVar();
  editorVar({
    ...editor,
    status: 'group-delete',
    groupEditing: group,
  });
}

export function mutationEditorStudentDelete(student) {
  const editor = editorVar();
  editorVar({
    ...editor,
    status: 'student-delete',
    questionsSelected: [],
    chapterSelected: null,
    studentEditing: student,
  });
}

export function mutationEditorFileImport() {
  const editor = editorVar();
  editorVar({
    ...editor,
    status: 'file-import',
  });
}
